import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { XMarkIcon, BookOpenIcon, SparklesIcon } from '@heroicons/react/24/outline';
import WhatIsAnAiServiceExplainer, {
  WHATS_AN_AI_SERVICE_TITLE,
} from '@/components/shared/WhatIsAnAiServiceExplainer';
import { docsGuideLink } from './HelpMenu';

/**
 * The "What's an AI Service?" explainer dialog (task 605).
 *
 * The Help menu entry used to deep-link to the explainer card inside the
 * first-run Dashboard checklist. That anchor only exists while the checklist is
 * on screen, so on a mature account — or after dismissal — the entry did
 * nothing useful. The entry now opens this dialog instead, following the help
 * assistant panel's pattern (task 545): the open state is owned by `AppLayout`,
 * so the explainer is reachable from any page at any account state, and closing
 * it returns the User to exactly where they were.
 *
 * The copy itself is `WhatIsAnAiServiceExplainer`, the same component the
 * checklist card renders — the two surfaces can never drift apart.
 */

/**
 * The published guide the footer points at. Documentation links are built with
 * `docsGuideLink`, so the base stays a config read and `?source=app-help` rides
 * along for the docs site's readership analytics (task 538, Pillar 3).
 */
export const WHATS_AN_AI_SERVICE_DOCS_SLUG = 'innovate-service-endpoints';

interface WhatIsAnAiServiceDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function WhatIsAnAiServiceDialog({ open, onClose }: WhatIsAnAiServiceDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          transition
          data-testid="whats-an-ai-service-dialog"
          className="w-full max-w-lg rounded-lg bg-white shadow-xl transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 dark:bg-gray-800"
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b border-gray-200 px-5 py-3 dark:border-gray-700">
            <DialogTitle className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
              <SparklesIcon className="h-4 w-4 text-indigo-500" />
              {WHATS_AN_AI_SERVICE_TITLE}
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              data-testid="whats-an-ai-service-close"
              aria-label="Close explainer"
              className="-mr-1.5 rounded-md p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Copy — the same component the first-run checklist card renders */}
          <div className="px-5 py-4">
            <WhatIsAnAiServiceExplainer />
          </div>

          {/* Where to go next */}
          <div className="flex flex-wrap items-center gap-4 border-t border-gray-100 px-5 py-3 dark:border-gray-700">
            <Link
              to="/services"
              onClick={onClose}
              data-testid="whats-an-ai-service-services-link"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              <SparklesIcon className="h-4 w-4" />
              Go to AI Services
            </Link>
            <a
              href={docsGuideLink(WHATS_AN_AI_SERVICE_DOCS_SLUG)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whats-an-ai-service-docs-link"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              <BookOpenIcon className="h-4 w-4" />
              Read the guide
            </a>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
